import { memo, useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Item from './Item';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';

export type ItemType = {
  id: string;
  uuid: UniqueIdentifier;
  name: string;
};

export type BaseSortProps = {
  data: any;
  render: (data: any, index: number, onItemChange: any) => React.ReactNode;
  onItemChange?: (item: ItemChangeProps) => void;
  disabled?: boolean;
  search: (data: any) => boolean;
};

export type ItemChangeProps = {
  id?: string;
  $id?: string;
  position?: string;
  data: any;
};

function BaseSort(props: BaseSortProps) {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [items, setItems] = useState<ItemType[]>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.data) {
      let uuid = Date.now();
      setItems(
        props.data.map((data: any) => ({
          ...data,
          uuid: ++uuid,
        }))
      );
    }
  }, [props.data]);
  //#endregion Implement Hook

  //#region Handle Function
  const findItem = (id: UniqueIdentifier | undefined) => {
    const item = items.find((item) => item.uuid === id);
    if (!item) return null;

    return item;
  };

  const findItemIndex = (id: UniqueIdentifier | undefined) => {
    return items.findIndex((item) => item.uuid === id);
  };
  //#endregion Handle Function

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const { id } = active;
    setActiveId(id);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    // Handling item Sorting
    if (active.id.toString() && over?.id.toString() && active && over && active.id !== over.id) {
      // Find the active and over column
      const activeContainer = findItem(active.id);
      const overContainer = findItem(over.id);

      if (!activeContainer || !overContainer) return;

      // Find the index of the active and over column
      const activeColumnIndex = items.findIndex((item) => item.uuid === activeContainer.id);
      const overColumnIndex = items.findIndex((item) => item.uuid === overContainer.id);

      // Find the index of the active and over item
      const activeItemIndex = items.findIndex((item) => item.uuid === active.id);
      const overItemIndex = items.findIndex((item) => item.uuid === over.id);
      // In the same column
      let newItems = [...items];
      if (activeColumnIndex === overColumnIndex) {
        newItems = arrayMove(newItems, activeItemIndex, overItemIndex);
      } else {
        // In different items
        const [removeditem] = newItems.splice(activeItemIndex, 1);
        newItems.splice(overItemIndex, 0, removeditem);
      }

      newItems = newItems.map((item, index) => {
        return {
          ...item,
          position: index + 1,
        };
      });

      setItems(newItems);
      const id = newItems.find((item) => item.uuid === active.id)?.id;
      if (props.onItemChange) {
        props.onItemChange({
          id: String(activeId),
          $id: id,
          position: String(overItemIndex + 1),
          data: cloneDeep(newItems).map((item) => {
            delete item['uuid' as keyof ItemType];
            return { ...item };
          }),
        });
      }
    }

    setActiveId(null);
  }

  function onItemChange(item: any, index: number) {
    let newItems = cloneDeep(items);
    if (!item) {
      newItems = newItems.filter((_item: any, itemIndex: number) => Number(itemIndex) !== Number(index));
    } else {
      newItems[index] = { ...newItems[index], ...item };
    }
    setItems(newItems);

    props.onItemChange &&
      props.onItemChange({
        data: cloneDeep(newItems).map((item) => {
          delete item['uuid' as keyof ItemType];
          return { ...item };
        }),
      });
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((i) => i.uuid)}>
        {items.map((item, index) => (
          <Box
            className={clsx({
              hidden: props?.search && !props?.search(item),
            })}
            key={item.uuid}
          >
            <Item
              data={item}
              index={index}
              id={item.uuid}
              render={props.render}
              disabled={props.disabled}
              onItemChange={onItemChange}
            />
          </Box>
        ))}
      </SortableContext>

      <DragOverlay adjustScale={false}>
        {activeId && activeId.toString() && (
          <Item
            index={findItemIndex(activeId)}
            data={findItem(activeId)}
            id={activeId}
            render={props.render}
            onItemChange={onItemChange}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default memo(BaseSort);
